import React from "react";
import ReactDOM from "react-dom";
import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { detect } from "detect-browser";
import logo from "./babelvizlogo.png";

const browser = detect();
const LICENSE_SERVER_DOMAIN = "https://api.keygen.sh";

/**
 * API client
 */
const KEYGEN_ACCOUNT_ID = process.env["KEYGEN_ACCOUNT_ID"];

if (!KEYGEN_ACCOUNT_ID) {
  throw Error("environment variable KEYGEN_ACCOUNT_ID is required");
}

const client = {
  KEYGEN_ACCOUNT_ID,

  async validateLicenseKeyWithFingerprint(key, fingerprint) {
    const res = await fetch(
      `${LICENSE_SERVER_DOMAIN}/v1/accounts/${KEYGEN_ACCOUNT_ID}/licenses/actions/validate-key`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Keygen-Version": "1.2",
        },
        body: JSON.stringify({
          meta: {
            scope: { fingerprint, entitlements: ["PORTAL"] },
            key,
          },
        }),
      }
    );

    const { meta, data, errors } = await res.json();

    return {
      meta,
      data,
      errors,
    };
  },

  async activateMachineForLicense(
    license,
    fingerprint,
    name,
    platform,
    browser,
    version
  ) {
    const res = await fetch(
      `${LICENSE_SERVER_DOMAIN}/v1/accounts/${KEYGEN_ACCOUNT_ID}/machines`,
      {
        method: "POST",
        headers: {
          Authorization: `License ${license.attributes.key}`,
          "Content-Type": "application/json",
          Accept: "application/json",
          "Keygen-Version": "1.2",
        },
        body: JSON.stringify({
          data: {
            type: "machine",
            attributes: {
              fingerprint,
              name,
              platform,
              metadata: {
                browser,
                version,
              },
            },
            relationships: {
              license: { data: { type: "license", id: license.id } },
            },
          },
        }),
      }
    );

    const { data, errors } = await res.json();

    return {
      data,
      errors,
    };
  },

  async deactivateMachineForLicense(license, id) {
    const res = await fetch(
      `${LICENSE_SERVER_DOMAIN}/v1/accounts/${KEYGEN_ACCOUNT_ID}/machines/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `License ${license.attributes.key}`,
          Accept: "application/json",
          "Keygen-Version": "1.2",
        },
      }
    );

    if (res.status === 204) {
      return {};
    }

    const { errors } = await res.json();

    return {
      errors,
    };
  },

  async listMachinesForLicense(license) {
    const res = await fetch(
      `${LICENSE_SERVER_DOMAIN}/v1/accounts/${KEYGEN_ACCOUNT_ID}/machines`,
      {
        method: "GET",
        headers: {
          Authorization: `License ${license.attributes.key}`,
          Accept: "application/json",
        },
      }
    );

    const { data, errors } = await res.json();

    return {
      data,
      errors,
    };
  },
};

/**
 * State management
 */
const createEmitter = () => {
  const subscriptions = new Map();
  return {
    emit: (v) => subscriptions.forEach((fn) => fn(v)),
    subscribe: (fn) => {
      const key = Symbol();
      const unsubscribe = () => subscriptions.delete(key);

      subscriptions.set(key, fn);

      return unsubscribe;
    },
  };
};

// See: https://formidable.com/blog/2021/stores-no-context-api/
const createStore = (init) => {
  const emitter = createEmitter();

  let store = null;
  const get = () => store;
  const set = (op) => ((store = op(store)), emitter.emit(store));
  store = init(set, get);

  const useStore = () => {
    const [localStore, setLocalStore] = useState(get());

    useEffect(() => emitter.subscribe(setLocalStore), []);

    return localStore;
  };

  return useStore;
};

const useLicensingStore = createStore((set, get) => ({
  key: "",
  placeholder: "XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-V3",
  validation: null,
  license: null,
  machines: [],
  errors: [],

  setKey: (key) => set((state) => ({ ...state, key })),

  validateLicenseKeyWithFingerprint: async () => {
    const { key } = get();
    const defaultFingerprint = "default-fingerprint";

    // Initial validation
    const initialValidation = await client.validateLicenseKeyWithFingerprint(
      key,
      defaultFingerprint
    );
    if (initialValidation.errors || !initialValidation.data) {
      return set((state) => ({
        ...state,
        validation: null,
        license: null,
        machines: [],
        errors: initialValidation.errors || [
          { code: "INVALID_LICENSE", detail: "Invalid license key" },
        ],
      }));
    }

    // Set initial license data
    set((state) => ({
      ...state,
      validation: initialValidation.meta,
      license: initialValidation.data,
      errors: [],
    }));

    // List machines for valid license
    const machinesResult = await client.listMachinesForLicense(
      initialValidation.data
    );
    if (machinesResult.errors) {
      return set((state) => ({ ...state, machines: [] }));
    }

    const machines = machinesResult.data;
    if (!machines.length) {
      return set((state) => ({ ...state, machines: [] }));
    }

    // Try validation with each machine's fingerprint
    let validationResult = null;
    for (const machine of machines) {
      const fingerprint = machine.attributes.fingerprint;
      try {
        const { meta, data, errors } =
          await client.validateLicenseKeyWithFingerprint(key, fingerprint);
        if (!errors) {
          validationResult = { meta, data };
          break;
        }
      } catch (error) {
        console.error(
          `Validation failed for fingerprint ${fingerprint}:`,
          error
        );
      }
    }

    // Update state with final validation result
    set((state) => ({
      ...state,
      validation: validationResult?.meta || initialValidation.meta,
      license: validationResult?.data || initialValidation.data,
      machines: machines,
      errors:
        !validationResult && machines.length
          ? [
              {
                code: "NO_VALID_FINGERPRINT",
                detail: "No machine fingerprints could validate the license",
              },
            ]
          : [],
    }));
  },

  activateMachineForLicense: async ({ name, platform, browser, version }) => {
    const {
      license,
      listMachinesForLicense,
      validateLicenseKeyWithFingerprint,
    } = get();

    const { errors } = await client.activateMachineForLicense(
      license,
      uuidv4(),
      name,
      platform,
      browser,
      version
    );
    if (errors) {
      client.listMachinesForLicense();
      return set((state) => ({ ...state, errors }));
    }

    set((state) => ({ ...state, errors: [] }));
    listMachinesForLicense();
    validateLicenseKeyWithFingerprint();
  },

  deactivateMachineForLicense: async (id) => {
    const {
      license,
      validateLicenseKeyWithFingerprint,
      listMachinesForLicense,
    } = get();

    const { errors } = await client.deactivateMachineForLicense(license, id);
    if (errors) {
      return set((state) => ({ ...state, errors }));
    }

    set((state) => ({ ...state, errors: [] }));
    listMachinesForLicense();
    validateLicenseKeyWithFingerprint();
  },

  listMachinesForLicense: async () => {
    const { license } = get();
    if (!license) return;

    const { data, errors } = await client.listMachinesForLicense(license);
    if (errors) {
      return set((state) => ({ ...state, errors }));
    }

    set((state) => ({
      ...state,
      machines: data,
    }));
  },

  clearError: (error) => {
    const { errors } = get();
    set((state) => ({ ...state, errors: errors.filter((e) => e !== error) }));
  },

  reset: () => {
    set((state) => ({
      ...state,
      key: "",
      validation: null,
      license: null,
      machines: [],
      errors: [],
    }));
  },
}));

const useDeviceInfoStore = createStore(() => ({
  platform: browser.os ?? window.navigator.platform,
  version: browser.version ?? window.navigator.appVersion,
  browser: browser.name ?? window.navigator.userAgent,
  name: `Demo Device ${uuidv4().substring(0, 5)}`,
}));

/**
 * Components
 */

const Logo = () => (
  <div style={{ position: "absolute", top: "20px", left: "15px" }}>
    <img
      src={logo} // Replace with your actual logo path
      alt="BabelViz logo"
      style={{ height: "25px" }} // Adjust size as needed
    />
  </div>
);

const LicenseInfo = ({ showSeats = true }) => {
  const { license, machines, validation, reset } = useLicensingStore();
  const created = license?.attributes?.created?.split("T")?.[0];
  const expiry = license?.attributes?.expiry?.split("T")?.[0];
  const shouldShowValidity = validation?.valid && machines.length > 0;

  return (
    <div className="demo-component">
      <h2>
        <small>{license?.attributes?.name ?? "License key"}</small>
        {license?.attributes?.key ?? "N/A"}
        {shouldShowValidity ? (
          <span className="demo-component__tag demo-component__tag--valid">
            Valid
          </span>
        ) : machines.length > 0 ? (
          <span className="demo-component__tag demo-component__tag--invalid">
            Invalid
          </span>
        ) : null}
      </h2>
      <div className="demo-component__table">
        <table>
          <thead>
            <tr>
              <th>Issued On</th>
              <th>Valid Until</th>
              {/* {showSeats ? <th># Seats</th> : null} */}
              {showSeats ? <th># Machines</th> : null}
              <th>Validation Code</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{created ?? "N/A"}</td>
              <td>{expiry ?? "N/A"}</td>
              {showSeats ? (
                <td>
                  <strong>
                    {machines.length}/{license?.attributes?.maxMachines || 0}
                  </strong>
                </td>
              ) : null}
              <td>
                <code>{validation?.code}</code>
              </td>
              <td>
                <button
                  className="demo-component__button demo-component__button--logout"
                  type="button"
                  onClick={(e) => reset()}
                >
                  Logout
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  const [confirmationText, setConfirmationText] = useState("");

  useEffect(() => {
    if (isOpen) {
      document.querySelector(".modal-content input")?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (confirmationText === "I wish to delete this machine") {
      onConfirm();
    } else {
      alert("Please type the exact confirmation phrase to proceed.");
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-content">
          <h2>Confirm Machine Deactivation</h2>
          <p>
            Your BabelViz instance will stop working if you accidentally
            deactivate its machine. Do you wish to proceed?
          </p>
          <input
            type="text"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder='Type "I wish to delete this machine"'
          />
          <div className="modal-actions">
            <button onClick={handleConfirm} className="confirm-button">
              Confirm
            </button>
            <button onClick={onClose} className="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const LicenseManager = () => {
  const { license, machines, deactivateMachineForLicense } =
    useLicensingStore();
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDeactivate = (machineId) => {
    setSelectedMachine(machineId);
    setIsModalOpen(true);
  };

  const handleConfirmDeactivation = () => {
    if (selectedMachine) {
      deactivateMachineForLicense(selectedMachine);
    }
    setIsModalOpen(false);
    setSelectedMachine(null);
  };

  return (
    <div className="demo-component">
      <h3>
        <small>Manage devices</small>
        Using {machines.length} of {license.attributes.maxMachines} machines
      </h3>
      <p className="demo-component__info">
        The current license{" "}
        {machines.length > 0 ? <em>has</em> : <em>has no</em>} machines
        registered.
      </p>
      <div className="demo-component__table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Fingerprint</th>
              <th>Activated On</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {machines.map((machine, index) => (
              <tr key={machine.id}>
                <td>
                  <code>{index + 1}</code>
                </td>
                <td>
                  <code>{machine.attributes.fingerprint}</code>
                </td>
                <td>{machine.attributes.created.split("T")[0]}</td>
                <td>
                  <button
                    className="demo-component__button demo-component__button--deactivate"
                    type="button"
                    onClick={() => handleDeactivate(machine.id)}
                  >
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDeactivation}
      />
    </div>
  );
};

const LicenseValidator = () => {
  const { key, placeholder, validateLicenseKeyWithFingerprint, setKey } =
    useLicensingStore();

  return (
    <div className="demo-component">
      <h2>
        <small>BabelViz Translator License Portal</small>
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          validateLicenseKeyWithFingerprint();
        }}
      >
        <input
          type="text"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder={placeholder}
        />
        <button type="submit">Continue</button>
      </form>
    </div>
  );
};

const LicenseActivationPortal = () => {
  const { license, validation, errors, machines } = useLicensingStore();

  if (errors?.length) {
    return (
      <div className="demo" data-title="Demo App">
        <LicenseErrors />
        <LicenseInfo />
        {errors.some((e) => e.code === "MACHINE_LIMIT_EXCEEDED") ? (
          <LicenseManager />
        ) : null}
      </div>
    );
  }

  if (license == null && validation == null) {
    return (
      <div className="demo" data-title="Demo App">
        <LicenseValidator />
      </div>
    );
  }

  if (validation?.valid || machines.length > 0) {
    return (
      <div className="demo" data-title="Demo App">
        <LicenseInfo />
        <LicenseManager />
      </div>
    );
  }

  return (
    <div className="demo" data-title="Demo App">
      <LicenseInfo />
    </div>
  );
};

// Add LicenseErrors component
const LicenseErrors = () => {
  const { errors, clearError } = useLicensingStore();

  return (
    <div className="demo-component demo-component--error">
      {errors.map((error) => (
        <div key={error.code} className="demo-component__error">
          <p>{error.detail}</p>
          <button onClick={() => clearError(error)}>Dismiss</button>
        </div>
      ))}
    </div>
  );
};

const root = document.getElementById("app-root");
if (root != null) {
  ReactDOM.render(
    <>
      <Logo />
      <LicenseActivationPortal />
    </>,
    root
  );
}
